import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Menu, X, User } from 'lucide-react';
import { navEintraege } from '../../data/navigation';
import { Avatar } from '../../data/avatare';
import { useCart } from '../../context/CartContext';

/**
 * FloatingHeader
 *
 * revealMode:
 *   'scroll' (default) — visible at top, hides when scrolling down, returns
 *      when scrolling up. The behaviour every standard window-scroll page
 *      uses (Home, Shop, Portfolio, etc.).
 *   'edge'             — hidden by default, slides in when the cursor enters
 *      the top ~80px of the viewport, and slides out again when it leaves
 *      (with a small grace so it doesn't flicker when the cursor leaves the
 *      header itself). Use on pages whose body isn't window-scrollable, like
 *      /builder and /games.
 *
 * Die Leiste (<header>) wird per Portal direkt in <body> gerendert. Die Seiten
 * binden sie zwar in ihr überblendetes motion.div ein (seitenBlende.js), die
 * Deckkraft-Blende beim Seitenwechsel erfasst sie so aber nicht: Sie bleibt
 * stehen, statt mit der Seite aus- und wieder einzublenden. Wegen
 * AnimatePresence mode="wait" ist immer nur die Leiste EINER Seite eingehängt.
 */
const FloatingHeader = ({ revealMode = 'scroll' }) => {
  // Always start visible. Both modes:
  //   - scroll: scroll handler will hide on first scroll-down
  //   - edge:   stays visible until the cursor moves below the top zone
  // This way, navigating *into* Builder/Games lands you with the header
  // already showing — you don't have to discover the top-edge trigger.
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { getItemCount, toggleCart } = useCart();

  const isEnglish = i18n.language === 'en' || location.pathname.startsWith('/en');
  const langPrefix = isEnglish ? '/en' : '';
  const cartItemCount = getItemCount();

  // ----- scroll-based reveal (default mode) ----------------------------------
  // Hide header on scroll down, show on scroll up. Always visible near top.
  useEffect(() => {
    if (revealMode !== 'scroll') return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 100) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        setIsVisible(false);
        setMobileMenuOpen(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, revealMode]);

  // ----- edge-hover reveal (Builder / Games / anywhere without window scroll)
  // The header is fully hidden until the cursor enters the top zone of the
  // viewport. Once visible it stays visible while the cursor is in the
  // top zone OR over the header itself; sliding out otherwise after a short
  // grace period (prevents flicker when the cursor leaves the header chip
  // but is still within the trigger zone).
  useEffect(() => {
    if (revealMode !== 'edge') return;

    const TOP_ZONE_PX = 80;        // cursor must be this close to the top
    const HIDE_DELAY_MS = 250;     // grace before hiding
    let hideTimer = null;

    const showImmediate = () => {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      setIsVisible(true);
    };
    const scheduleHide = () => {
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        setMobileMenuOpen(false);
      }, HIDE_DELAY_MS);
    };

    const onMove = (e) => {
      const inTopZone = e.clientY <= TOP_ZONE_PX;
      // Also keep it open if the cursor is over the header itself — its bbox
      // can extend below TOP_ZONE_PX since it's positioned at top:5 + ~60px.
      const overHeader = e.target.closest && e.target.closest('[data-testid="floating-header"]');
      if (inTopZone || overHeader) showImmediate();
      else scheduleHide();
    };

    // If the cursor leaves the window entirely, hide promptly.
    const onLeave = () => scheduleHide();

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [revealMode]);

  // Das Logo fuehrt nach Hause, sonst nichts. Frueher oeffnete ein
  // Dreifachklick heimlich das Portal — ein Rest aus der Zeit, als es keinen
  // sichtbaren Zugang gab. Jetzt steht der Konto-Knopf daneben, und ein
  // verstecktes Kunststueck sorgt nur noch fuer versehentliche Spruenge.
  // Logo und Leistenpunkte sind echte Links (<Link>): Mittelklick und „in neuem
  // Tab öffnen“ gehen, Suchmaschinen sehen die Ziele. Ein Klick schließt nur
  // noch das Handy-Menü, den Wechsel macht der Router (die Seiten blenden selbst).
  const startseite = isEnglish ? '/en' : '/';
  const menueZu = () => setMobileMenuOpen(false);

  // Welche Punkte sichtbar sind, steht in data/navigation.js — dieselbe Liste nutzt
  // die Kachel-Navigation auf der Startseite, damit nicht die eine Seite zeigt, was
  // die andere ausgeblendet hat. (Kostenlos liegt englisch auf /free, das regelt
  // navEintraege mit; sonst liefe `/en` + `/kostenlos` ins Leere.)
  const navItems = navEintraege(isEnglish);

  // Anmeldung: fuehrt auf die Aether-Anmeldeseite. Liegt schon eine Sitzung im
  // Speicher, heisst der Knopf "Dashboard" — geschuetzt wird drueben im Gate, hier
  // geht es nur um die Beschriftung. Der Header haengt ausserhalb des Auth-Providers,
  // deshalb der direkte Blick in den Speicher statt useAetherAuth().
  const [hatSitzung, setHatSitzung] = useState(false);
  // Das eigene Profilbild, damit der Knopf zeigt, WER angemeldet ist, statt
  // nur DASS jemand angemeldet ist.
  const [meinAvatar, setMeinAvatar] = useState(null);
  useEffect(() => {
    const pruefen = () => {
      try {
        const token = localStorage.getItem('aether_token');
        setHatSitzung(!!token);
        if (!token) { setMeinAvatar(null); return; }
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/aether/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => (r.ok ? r.json() : null))
          .then(d => setMeinAvatar((d && (d.avatar || (d.user && d.user.avatar))) || null))
          .catch(() => {});
      } catch { setHatSitzung(false); }
    };
    pruefen();
    window.addEventListener('storage', pruefen);
    return () => window.removeEventListener('storage', pruefen);
  }, [location.pathname]);

  const kontoLabel = hatSitzung ? 'Dashboard' : (isEnglish ? 'Sign in' : 'Anmelden');

  // Edge mode (Builder/Games) gets a pure slide — no opacity fade, no
  // backdrop-filter "warming up" mid-animation, no two-layer staging.
  // Scroll mode keeps its original opacity + transform combo because it
  // sits on top of regular page content (not the 3D viewport) and the
  // crossfade reads fine there.
  const edgeMode = revealMode === 'edge';
  const headerStyle = {
    opacity: edgeMode ? 1 : (isVisible ? 1 : 0),
    transform: isVisible
      ? 'translateX(-50%) translateY(0)'
      // -150% on edge mode pushes the chip fully past top:5 + h-[60px] so
      // nothing peeks through with opacity locked to 1.
      : `translateX(-50%) translateY(${edgeMode ? '-150%' : '-100%'})`,
    pointerEvents: isVisible ? 'auto' : 'none',
    transition: edgeMode
      ? 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)'
      : 'opacity 400ms, transform 400ms',
  };

  return (
    <>
      {/* Floating Header — per Portal in <body>, damit die Seitenblende sie nicht erfasst */}
      {createPortal(
      <header
        data-testid="floating-header"
        className="fixed top-5 left-1/2 z-[1000]"
        style={headerStyle}
      >
        <nav
          // In edge mode (Builder / Games) the page behind the header is a
          // colourful 3D viewport. `liquid-glass-strong` uses
          // background-blend-mode: luminosity + a 40px backdrop blur + a
          // gradient ::before border. Stacked over the 3D scene that reads
          // as two competing colours and a "wrapped" look. Use a flat dark
          // chip with a simple blur there instead.
          className={
            revealMode === 'edge'
              ? 'flex items-center gap-2 sm:gap-4 px-4 sm:px-6 h-[60px] rounded-full overflow-visible bg-[#0a0a0a]/92 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.45)]'
              : 'flex items-center gap-2 sm:gap-4 px-4 sm:px-6 h-[60px] rounded-full liquid-glass-strong overflow-visible'
          }
        >
          {/* Logo */}
          <Link
            to={startseite}
            onClick={menueZu}
            data-testid="header-logo"
            className="flex items-center gap-2 transition-all duration-300 hover:opacity-80"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-white/10">
              <img
                src="/logo-badge-192.webp"
                alt="David Media"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-syne font-bold text-white text-base hidden sm:block">
              DAVIDMEDIA
            </span>
          </Link>

          {/* Desktop Navigation */}
          {/* Sechs Punkte brauchen gut 950 px: erst ab lg (1024) als Leiste, darunter im Menü */}
          <div className="hidden lg:flex items-center gap-1 ml-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={`${langPrefix}${item.path}`}
                onClick={menueZu}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="relative px-4 py-2 text-sm font-manrope font-medium whitespace-nowrap text-[#888888] hover:text-white transition-colors duration-300 group"
              >
                {item.label}
                {/* Animated underline */}
                <span 
                  className="absolute bottom-1 left-4 right-4 h-[1px] bg-[#F97316] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                />
              </Link>
            ))}
          </div>

          {/* Right side: Anmeldung + Cart */}
          <div className="flex items-center gap-2 ml-auto overflow-visible">
            {/* Konto — am Rechner mit Beschriftung, auf dem Handy nur das
                Bild: dort ist fuer Text kein Platz, und ein runder Knopf neben
                dem Warenkorb sieht gewollt aus statt gequetscht. Angemeldet
                steht das eigene Profilbild darin, dann sieht man auch WER. */}
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/aether'); }}
              data-testid="nav-login-btn"
              title={kontoLabel}
              aria-label={kontoLabel}
              className="flex items-center justify-center md:justify-start gap-0 md:gap-2 w-10 md:w-auto h-10 px-0 md:px-4 rounded-full transition-all duration-300 hover:scale-105 bg-[rgba(12,10,9,0.72)] border border-white/[0.14] backdrop-blur-md text-[#E4E4E7] hover:text-white overflow-hidden"
            >
              {hatSitzung && meinAvatar
                ? <Avatar id={meinAvatar} size={28} className="shrink-0" />
                : <User className="w-4 h-4 shrink-0" />}
              <span className="hidden md:inline text-sm whitespace-nowrap">{kontoLabel}</span>
            </button>

            {/* Cart Button with Badge */}
            <div className="relative">
              <button
                onClick={toggleCart}
                data-testid="cart-button"
                className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-105 bg-[rgba(12,10,9,0.72)] border border-white/[0.14] backdrop-blur-md"
              >
                <ShoppingCart className="w-4 h-4 text-[#E4E4E7]" />
              </button>
              {cartItemCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-black pointer-events-none"
                  style={{ background: 'var(--primary)' }}
                >
                  {cartItemCount}
                </span>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-105 bg-[rgba(12,10,9,0.72)] border border-white/[0.14] backdrop-blur-md"
            >
              {mobileMenuOpen ? (
                <X className="w-4 h-4 text-white" />
              ) : (
                <Menu className="w-4 h-4 text-[#E4E4E7]" />
              )}
            </button>
          </div>
        </nav>
      </header>,
      document.body
      )}

      {/* Mobile Menu Overlay — bleibt in der Seite: beim Wechsel über das Menü blendet es
          mit der alten Seite aus, statt am Ende der Blende hart zu verschwinden */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-[999] lg:hidden transition-all duration-500 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          background: 'rgba(3, 3, 3, 0.95)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navItems.map((item, index) => (
            <Link
              key={item.path}
              to={`${langPrefix}${item.path}`}
              onClick={menueZu}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className="font-manrope text-4xl font-extrabold tracking-tight text-white hover:text-[#F97316] transition-all duration-300"
              style={{
                opacity: mobileMenuOpen ? 1 : 0,
                transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: `${index * 100}ms`,
              }}
            >
              {item.label}
            </Link>
          ))}
          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/aether'); }}
            data-testid="nav-login-btn-mobile"
            className="font-manrope text-4xl font-extrabold tracking-tight text-white hover:text-[#F97316] transition-all duration-300 flex items-center gap-3"
            style={{
              opacity: mobileMenuOpen ? 1 : 0,
              transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: `${navItems.length * 100}ms`,
            }}
          >
            <User className="w-7 h-7" /> {kontoLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default FloatingHeader;
